import { defineStore } from 'pinia'
import { createState, type PromoEngineState } from './state'

// Types
import {
  ApiV1SalesDataAttributesPagamenti,
  InlineResponse2006,
  InlineResponse2007,
  type PromoEngineApplyResponse,
  type PromoEnginePreviewResponseDataAttributes,
  type SalesData,
} from '@/api'

// Client API
import {
  applyPromotionsAuto,
  applyPromotionsManual,
  estimatePromotionsManual,
  evaluatePromotionsAuto,
  getCampaigns,
} from './../promo-engine-client'
import { salesApi } from '@/addons/axios'

// VUEX: import del root store
import rootVuex from '@/store'
import { ConfigGetters } from '@/store/configs-store'
import {
  SalesActions,
  SalesGetters,
  SalesMutations,
} from '@/store/sales/sales-store'
import { NotificationsActions } from '@/store/notifications-store'
import { LoyaltyGetters, LoyaltyMutations } from '@/modules/loyalty/store'

// i18n
import i18n from '@/addons/i18n'
import { nowIsoLocalWithOffsetHHMM } from '@/addons/date'
import { isEmpty } from 'lodash'
import {
  buildPromoEngineDetailsFromSource,
  updateCapiBasedOnPreview,
} from '@/modules/promo-engine/addons/promotions'
import { roundDecimals } from '@/addons/adjustments'
import { PromoOutput, PromoPayload } from '@/modules/promo-engine/types'

export const usePromoEngineStore = defineStore('promo-engine', {
  state: (): PromoEngineState => ({
    ...createState(),
  }),

  /*
   * GETTERS Pinia
   */
  getters: {
    // IS_PROMO_ENGINE_ACTIVE -> isPromoEngineActive
    isPromoEngineActive(): boolean {
      const cfg = rootVuex.getters?.[ConfigGetters.GET_STORE_CONFIGS]
      return (
        cfg?.PROMO_ENGINE_ENABLED === '2' &&
        cfg?.PROMO_ENGINE_USER_SELECTION === '0'
      )
    },

    isPromoEngineFidelityActive(): boolean {
      const cfg = rootVuex.getters?.[ConfigGetters.GET_STORE_CONFIGS]
      return (
        cfg?.PROMO_ENGINE_ENABLED === '2' &&
        cfg?.PROMO_ENGINE_FIDELITY_ENABLED === '1'
      )
    },

    // IS_PROMO_ENGINE_PREVIEW_ENABLED -> isPromoEnginePreviewEnabled
    isPromoEnginePreviewEnabled(): boolean {
      const cfg = rootVuex.getters?.[ConfigGetters.GET_STORE_CONFIGS]
      // la preview in manual sarà attiva quando promo enginw = 2 + manual attivo + preview attiva
      // TODO: verificare questo controlllo con i requirements: se l'anteprima è subordinata alla presenza attiva della modalità manuale
      return (
        cfg?.PROMO_ENGINE_ENABLED === '2' &&
        cfg?.PROMO_ENGINE_USER_SELECTION === '1' && // questo controllo può essere rismosso se l'anteprima non è correlata alla manuale
        cfg?.PROMO_ENGINE_PREVIEW_ENABLED === '1'
      )
    },

    // IS_PROMO_ENGINE_MANUAL_ACTIVE -> isPromoEngineManualActive
    isPromoEngineManualActive(): boolean {
      const cfg = rootVuex.getters?.[ConfigGetters.GET_STORE_CONFIGS]
      return (
        cfg?.PROMO_ENGINE_ENABLED === '2' &&
        cfg?.PROMO_ENGINE_USER_SELECTION === '1'
      )
    },

    // GET_HAS_PROMO_ENGINE_ACTIVE -> getHasPromoEngineActive
    getHasPromoEngineActive(): boolean {
      const currentSale = rootVuex.getters?.[SalesGetters.GET_CURRENT_SALE]
      try {
        const dati_documenti = JSON.parse(currentSale?.dati_documenti ?? '{}')
        return (
          Object.keys(dati_documenti).length > 0 &&
          'promo_engine' in dati_documenti
        )
      } catch {
        return false
      }
    },

    // GET_PREVIEW_PROMO_ENGINE -> getPreviewPromoEngine
    getPreviewPromoEngine(): Array<PromoEnginePreviewResponseDataAttributes> {
      return this.previewPromoEngine
    },

    // get list of promo (promo MANUALE)
    getManualPromoList(): any {
      return this.manualPromoList
    },

    // Check Promo manuali attivabili
    hasActivableManualPromos: (state): boolean => {
      const list = state.manualPromoList ?? []
      return (
        Array.isArray(list) &&
        list.some((p) => Array.isArray(p?.effects) && p.effects.length > 0)
      )
    },
  },

  /*
   * ACTIONS Pinia
   */
  actions: {
    // RESET_STATE -> resetPromoEngineStore
    resetPromoEngineStore() {
      const fresh = createState()
      this.previewPromoEngine = fresh.previewPromoEngine
      this.manualPromoList = fresh.manualPromoList
      this.consumerCampaigns = fresh.consumerCampaigns
      this.manualModalShown = fresh.manualModalShown
      this.fidelityRecap = fresh.fidelityRecap
      this.saleDatiAggiuntivi = fresh.saleDatiAggiuntivi
    },

    clearCurrentSalePromos() {
      const fresh = createState()
      this.previewPromoEngine = fresh.previewPromoEngine
      this.manualPromoList = fresh.manualPromoList
      this.currentEstimate = fresh.currentEstimate
      this.currentManualPreview = fresh.currentManualPreview
      this.fidelityRecap = fresh.fidelityRecap
      this.saleDatiAggiuntivi = fresh.saleDatiAggiuntivi
    },

    // APPLY_PROMOTIONS_AUTO -> applyPromotionsAuto
    // TODO: deprecated?
    async applyPromotionsAuto(payload: SalesData) {
      try {
        const resp: PromoEngineApplyResponse =
          await applyPromotionsAuto(payload)
        await rootVuex.dispatch(
          SalesActions.SET_SALE_DATI_PROMO,
          (resp as any)?.data?.attributes,
        )
        return resp
      } catch {
        // Notifiche già in clientapi
        return undefined
      }
    },

    // // EVALUATE_PROMOTIONS_AUTO -> evaluatePromotionsAuto
    async evaluateAuto(payload, checkout?: string) {
      try {
        const resp = await evaluatePromotionsAuto(payload, checkout)
        if (checkout) {
          this.buildFidelityRecap(resp?.output)
        }
        return resp
      } catch {
        // Notifiche già in clientapi
        return undefined
      }
    },

    // GET_CAMPAIGNS_PROMOTIONS -> getCampaignsPromotions
    async getCampaignsPromotions(): Promise<boolean> {
      try {
        const storeCode = rootVuex.state?.configs?.currentStore?.STORE_CODE
        const consumer = rootVuex.state.sales?.currentSale?.pk_consumer
        const resp = await getCampaigns(
          storeCode, // filterRetailCode
          'RUNNING,EXPIRING', // filterLifecycle
          undefined, // filterActive
          1, // filterConditions
          undefined, // filterStartDate
          undefined, // filterEndDate,
          undefined, // filterPage,
          undefined, // filterSize,
          undefined, // filterLanguage,
          consumer, // filterConsumerCode,
          undefined, // filterIsJob,
          undefined, // params,
        )
        const results = resp?.results ?? []

        // commit in stato Pinia
        this.consumerCampaigns = results
        return results.length > 0
      } catch {
        // Notifiche già in clientapi
        return false
      }
    },

    // AUTO PROMO
    async _getSalePromotionsAuto(payload): Promise<boolean> {
      try {
        const evaluated = await this.evaluateAuto(payload)
        const resp = mapEvaluateResponseToPreview(evaluated)

        // selezione/normalizzazione AUTO: items + costs
        const items = Array.isArray(resp?.items) ? resp.items : []
        const costs = Array.isArray(resp?.costs) ? resp.costs : []

        // fail-fast: nessuna promo disponibile
        if (!items.length && !costs.length) {
          await rootVuex.dispatch(
            NotificationsActions.NOTIFY_WARN,
            i18n.global.t('pos_promo_engine.no_available_promotions_alert'),
          )
          return false
        }

        // check unico su "modified"
        const anyModified =
          items.some((i: any) => i?.modified) ||
          costs.some((c: any) => c?.modified)

        if (!anyModified) {
          await rootVuex.dispatch(
            NotificationsActions.NOTIFY_WARN,
            i18n.global.t('pos_promo_engine.no_available_promotions_alert'),
          )
          return false
        }

        // commit preview
        this.previewPromoEngine = resp
        return true
      } catch {
        // Notifiche già gestite nel client API
        return false
      }
    },

    // MANUAL PROMO
    async _getSalePromotionsManual(payload): Promise<boolean> {
      try {
        const resp = await estimatePromotionsManual(payload)

        // output manuale
        const list = (resp as any)?.output ?? []

        if (!Array.isArray(list) || list.length === 0) {
          await rootVuex.dispatch(
            NotificationsActions.NOTIFY_WARN,
            i18n.global.t('pos_promo_engine.no_available_promotions_alert'),
          )
          return false
        }

        // commit lista manuale
        this.manualPromoList = list

        // salvo tutta la resp per la apply successiva
        this.currentEstimate = {
          input: (resp?.input as PromoPayload) ?? {},
          output: (resp?.output as PromoOutput) ?? {},
          rule_engine_version: resp?.rule_engine_version ?? '',
          maxed_out_promotions: resp?.maxed_out_promotions ?? {},
        }
        return true
      } catch {
        // Notifiche già gestite nel client API
        return false
      }
    },

    // HELPERS

    /**
     * Costruisce il payload per AUTO, assicurando:
     * - normalizzazione dei capi (codice_stato = 'CURRENT', rimozione campi effimeri)
     * - creazione della sale su backend se manca id_transazione
     * - id composto come `${cod_negozio},${id_transazione}`
     */
    async _buildAutoPayloadFromCurrentSale(): Promise<SalesData> {
      const getCurrentSale = rootVuex.getters?.[SalesGetters.GET_CURRENT_SALE]
      const saleData = getCurrentSale

      // normalizzazione capi
      if (Array.isArray(saleData?.capi)) {
        saleData.capi = saleData.capi.map((d: any) => {
          d.codice_stato = 'CURRENT'
          if ('layby_delete' in d) {
            delete d.layby_delete
          }
          if ('error_code' in d) {
            delete d.error_code
          }
          return d
        })
      }

      // crea sale se manca id_transazione
      const hasId =
        Object.prototype.hasOwnProperty.call(saleData, 'id_transazione') &&
        saleData.id_transazione !== undefined

      if (!hasId) {
        const sale = await salesApi.apiV1SalesPost({
          data: { type: 'sale', attributes: saleData },
          preserve_decimals: true,
        })
        await rootVuex.dispatch(
          SalesActions.STORE_CREATION_DATE,
          (sale as any)?.data?.data?.attributes?.data_creazione,
        )
        await rootVuex.dispatch(
          SalesActions.STORE_ID_TRANSAZIONE,
          (sale as any)?.data?.data?.attributes?.id_transazione,
        )
      }

      // ricava id aggiornato
      const saleId = getCurrentSale.id_transazione

      return {
        data: {
          type: 'sales',
          id: `${getCurrentSale?.cod_negozio},${saleId}`,
          attributes: getCurrentSale,
        },
        preserve_decimals: true,
      } as SalesData
    },

    // GET_SALE_PROMOTIONS -> getSalePromotions
    async getSalePromotions(): Promise<boolean> {
      // puliamo lo stato prima di una nuova preview
      this.clearCurrentSalePromos()

      try {
        if (this.isPromoEngineManualActive) {
          // blocchiamo apertura modale di scelta (unico punto)
          this.manualModalShown = true

          // flusso MANUAL
          const payload = await this._buildManualPayloadFromCurrentSale()
          return await this._getSalePromotionsManual(payload)
        }
        // flusso AUTO (usa payload carrello da manuale)
        const payload = await this._buildManualPayloadFromCurrentSale()
        return await this._getSalePromotionsAuto(payload)
      } catch {
        // Notifiche già gestite a livello di client API
        return false
      }
    },

    /**
     * Costruisce il payload "manual estimate":
     * - normalizzazione dei capi (codice_stato = 'CURRENT', rimozione campi effimeri)
     * - creazione della sale su backend se manca id_transazione
     * - session_code composto da ${id_transazione}`
     */

    async _buildManualPayloadFromCurrentSale(): Promise<any> {
      // ---- DA STORE VUEX CORE ----|
      const cfg = rootVuex.getters?.[ConfigGetters.GET_STORE_CONFIGS]
      // const storeInfo = rootVuex.state?.configs?.currentStore ?? {}
      const getCurrentSale = rootVuex.getters?.[SalesGetters.GET_CURRENT_SALE]
      const saleData = getCurrentSale
      const consumer = rootVuex.getters?.[SalesGetters.GET_CONSUMER]
      const loyalty = rootVuex.getters?.[LoyaltyGetters.GET_ENABLED_CARD]
      // i buoni loyalty sono sconti loyalti specifici per il brand EMME
      const loyaltyRewards = cfg?.STORE_SIGN === 'LM'
      // ---- normalizzazione capi come AUTO ----
      if (Array.isArray(saleData?.capi)) {
        saleData.capi = saleData.capi.map((d: any) => {
          d.codice_stato = 'CURRENT'
          if ('layby_delete' in d) {
            delete d.layby_delete
          }
          if ('error_code' in d) {
            delete d.error_code
          }
          return d
        })
      }

      // ---- crea sale se manca id_transazione, come AUTO ----
      const hasId =
        Object.prototype.hasOwnProperty.call(saleData, 'id_transazione') &&
        saleData.id_transazione !== undefined

      if (!hasId) {
        const sale = await salesApi.apiV1SalesPost({
          data: { type: 'sale', attributes: saleData },
          preserve_decimals: true,
        })
        await rootVuex.dispatch(
          SalesActions.STORE_CREATION_DATE,
          (sale as any)?.data?.data?.attributes?.data_creazione,
        )
        await rootVuex.dispatch(
          SalesActions.STORE_ID_TRANSAZIONE,
          (sale as any)?.data?.data?.attributes?.id_transazione,
        )
      }
      // ricava id aggiornato

      // ---- shop info ---->
      const retail_code = cfg?.STORE_CODE
      const shop_country = cfg?.COUNTRY_CODE
      const shop_sign = cfg?.STORE_SIGN

      // ---- id / session ---->
      const idTransazione = String(saleData?.id_transazione ?? '')
      const code = idTransazione

      // ---- DATE ---->
      const creationDatetimeStr = saleData.data_creazione as string // "2025-10-01 15:39:47"
      // creation_date 'yyyyMMddHHmmss'
      const creation_date = creationDatetimeStr.replace(/[-: ]/g, '') // -> "20251001153947"

      // request_timestamp locale senza offset: 'yyyy-MM-ddTHH:mm:ss'
      // TODO: capire se e come inviare UTC +timeZone es.+2 request_timestamp: '2025-08-25T13:05:48+0200'
      const request_timestamp = nowIsoLocalWithOffsetHHMM()

      // ---- consumer (GET_CONSUMER) ---->
      const birth_day = String(consumer?.giorno_nascita ?? '')
      const birth_month = String(consumer?.mese_nascita ?? '')
      const birth_year = String(consumer?.anno_nascita ?? '')
      const email = String(consumer?.email ?? '')
      const tel_1 = String(consumer?.telefono1 ?? '')
      const residence_state = String(consumer?.nazione_iso ?? '')

      // ---- loyalty (GET_ENABLED_CARD) ---->
      const card_type = String(loyalty?.chiave ?? '')
      const card_code = String(loyalty?.valore ?? '')
      const card_level = String(loyalty?.fascia_attuale ?? '')
      const card_real_balance = loyalty?.current_value ?? 0
      const card_state = String(loyalty?.status ?? '')
      const issue_date = loyalty?.data_inizio
        ? `${loyalty.data_inizio}000000`
        : ''
      const expiration_date = loyalty?.data_fine
        ? `${loyalty.data_fine}000000`
        : ''

      const hasPromoDiscount = saleData?.pagamenti?.filter(
        (c) => c.codice_movimento === 'CONTABILITA_PROMOZIONE',
      )
      const promoDiscount = hasPromoDiscount.reduce(
        (total, item) => total + item.importo_finale,
        0,
      )

      const saleHasTipoVItems = saleData.capi.filter(
        (item) => item.tipo_importo === 'V',
      )
      const allVItemsFinalPrices = saleHasTipoVItems.reduce(
        (finalPrices, singleItem) => finalPrices + singleItem.importo_finale,
        0,
      )

      // ---- CAPI/items ---->
      const parseCustom = (s?: string) => {
        try {
          return s ? JSON.parse(s) : {}
        } catch {
          return {}
        }
      }

      let allDiscounts = 0
      const lastVIndex = saleData.capi.findLastIndex(
        (c) => c.tipo_importo === 'V',
      )
      const items = Array.isArray(saleData?.capi)
        ? saleData.capi.map((r: any, index: number) => {
            const cd = parseCustom(r?.custom_data)
            const annostag = String(cd?.annostag ?? '')
            const year = annostag.slice(0, 4) || ''
            const season = annostag.slice(4) || ''
            const retailBase = r?.importo_iniziale ?? 0
            const finalPrice = r?.importo_finale ?? retailBase
            const isLastCapoV = index === lastVIndex

            let discount_fidelity = 0
            if (r?.tipo_importo === 'V') {
              if (isLastCapoV) {
                discount_fidelity = promoDiscount - allDiscounts
              } else {
                discount_fidelity = roundDecimals(
                  (r?.importo_finale * promoDiscount) / allVItemsFinalPrices,
                )
                allDiscounts = allDiscounts + discount_fidelity
              }
            }

            const finalPriceWithBuonoDiscount =
              roundDecimals(finalPrice) + discount_fidelity

            return {
              shop_barcode: r?.sku, // ?? r?.sku_read ?? r?.ean ?? '',
              id: r?.progressivo,
              style_code: cd?.modello ?? '',
              style_code_variant: cd?.modello
                ? `${cd.modello}${cd.variante ?? ''}`
                : '',
              style_name: r?.nome ?? '',
              style_variant: cd?.variante ?? '',
              size: cd?.desc_taglia ?? '',
              year,
              season,
              class: r?.classe ?? '',
              exit: '', //  TODO: ? NON SO
              discount_on_item: r?.sconto ?? 0,
              collection: '', //  TODO: ? NON SO
              brand: '', //  TODO: ? NON SO
              seasonality: 'SEASONAL', //  TODO: ? NON SO
              product_category: r?.tipologia_merce ?? '',
              retail_price: retailBase,
              base_price: retailBase,
              pricelist_type: r?.tipo_importo ?? '', // ?? 'V',
              final_price: roundDecimals(finalPriceWithBuonoDiscount),
              original_final_price: finalPrice,
              tailor_expenses: 0,
              discount_fidelity: roundDecimals(discount_fidelity),
            }
          })
        : []

      const purchasedGiftCards = saleData?.pagamenti?.filter(
        (pagamento: ApiV1SalesDataAttributesPagamenti) =>
          pagamento.codice_movimento === 'CONTABILITA_GIFT' &&
          pagamento.cod_operazione === 'GIFT_CARD',
      )

      const mappedPurchasedGC = purchasedGiftCards.map(
        (item: ApiV1SalesDataAttributesPagamenti, giftCardIndex: number) => {
          return {
            id: giftCardIndex + 1,
            original_final_price: item.importo_finale,
            cost_amount: item.importo_finale,
            cost_type: item.codice_movimento,
            cost_type_trad: item.cod_operazione,
          }
        },
      )

      // ---->
      //  payload input manual
      // ---->
      const fullPayload = {
        config: {
          language: cfg?.GUI_LANGUAGE_LOCALE,
          datetime_format: 'FLAT_DATETIME', // TODO:  ? NON SO
        },
        shop: { retail_code, shop_country, shop_sign },
        status: 'OPEN', // saleData?.codice_stato, // 'OPEN', // TODO: ? NON SO
        code,
        session_code: code,
        creation_date,
        request_timestamp,
        consumer: {
          consumer_code: saleData?.pk_consumer ?? '',
          name: consumer?.nome ?? undefined,
          surname: consumer?.cognome ?? undefined,
          birth_day,
          birth_month,
          birth_year,
          email,
          tel_1,
          residence_state,
          fidelity: {
            card_type,
            card_code,
            card_level: card_level || undefined,
            card_real_balance: card_real_balance,
            card_state: card_state || undefined,
            issue_date: issue_date || undefined,
            expiration_date: expiration_date || undefined,
          },
        },
        payments: this.formatPayments(saleData?.pagamenti),
        type_cart: saleData?.codice_movimento, // SALE
        shipping_method: {},
        items,
        costs: purchasedGiftCards.length > 0 ? mappedPurchasedGC : [],
        discount_fidelity:
          hasPromoDiscount && !loyaltyRewards ? promoDiscount : 0,
        used_discounts: loyaltyRewards
          ? hasPromoDiscount?.map((e) => ({
              value: e.importo_finale,
            }))
          : [],
      }

      return fullPayload
    },

    /**
     * Apply manual (preview o checkout)
     * persist=false  → preview (checkout undefined)
     * persist=true   → checkout = '1' (salvataggio lato promo)
     */
    async applyManual(
      selectedPromos: { code: string; [k: string]: any }[],
      persist = false,
      sessionCode?: string,
    ) {
      let input: any
      // 1) input: usa quello della currentEstimate se presente, altrimenti ricostruisce
      input = isEmpty(this.currentEstimate?.input)
        ? (await this._buildManualPayloadFromCurrentSale())?.input
        : this.currentEstimate?.input

      // 2) sessionCode: logica necessaria per B2E, se sessionCode + persist presenti sostituisce al vecchio code
      if (persist && sessionCode) {
        input = { ...input, code: sessionCode, session_code: sessionCode }
      }

      // 3) destrutturo per cambio la chiave code --> promo
      const promotions = selectedPromos.map(({ code, ...sPromo }) => ({
        promo: code,
        ...sPromo,
      }))

      const payload = { input, promotions }

      // 4) chiamata client: preview (undefined) o checkout ('1')
      const checkout = persist ? '1' : undefined
      const resp = await applyPromotionsManual(payload, checkout)

      this.currentManualPreview = mapApplyResponseToPreview(resp)

      if (persist === true) {
        this.buildFidelityRecap(resp?.output)
      }
      return this.currentManualPreview
    },

    /**
     * Update values in payments drawer to account for GIFT CARDS bonus points.
     * This employs a variety of methods from apply-manual, evaluate-auto and accounts for different payments
     */
    async recalibrateBonusPoints() {
      // 1) input: ricostruisce
      const getCurrentSale = rootVuex.getters?.[SalesGetters.GET_CURRENT_SALE]
      const dati_documenti = JSON.parse(getCurrentSale?.dati_documenti ?? '{}')
      const promoList = Object.keys(dati_documenti?.promo_engine?.promo ?? {})
      const appliedPromos = this.manualPromoList.filter((promo) =>
        promoList?.includes(promo.code),
      )

      const correctPromoInput = this.isPromoEngineManualActive
        ? this.currentEstimate.input
        : this.previewPromoEngine.input

      // TODO: capire se questo arrotondamento si può togliere
      correctPromoInput!.items.forEach((capo) => {
        const findItemBySku = getCurrentSale.capi.find(
          (capoItem) =>
            capoItem.sku === capo.shop_barcode &&
            capoItem.progressivo === capo.id,
        )

        if (findItemBySku) {
          capo.final_price = roundDecimals(
            findItemBySku.importo_iniziale + capo.discount_fidelity,
          )
          capo.original_final_price = roundDecimals(
            findItemBySku.importo_iniziale,
          )
        }
      })

      correctPromoInput!.payments = this.formatPayments(
        getCurrentSale.pagamenti,
      )

      // 2) destrutturo per cambio la chiave code --> promo
      const promotions = appliedPromos.map(({ code, ...sPromo }) => ({
        promo: code,
        ...sPromo,
      }))
      const payload = this.isPromoEngineManualActive
        ? { input: correctPromoInput, promotions }
        : correctPromoInput

      let response: InlineResponse2006 | InlineResponse2007 | undefined | void
      // 3) chiamata client: apply se MANUAL o evaluate se AUTO
      try {
        if (this.isPromoEngineManualActive) {
          const resp = await applyPromotionsManual(payload)
          response = resp
        } else {
          const resp = await evaluatePromotionsAuto(payload)
          response = resp
        }
      } catch {
        // Notifiche già gestite a livello di client API
        return false
      }
      // 4) aggiorna store: punti in fidelity recap + preview
      const mappedResponse = mapEvaluateResponseToPreview(response)
      this.previewPromoEngine = mappedResponse
      this.buildFidelityRecap(response?.output)

      // 5) details in promo_engine in dati_documenti: aggiorna punti omaggio
      const updatedPromoEngineDetails = buildPromoEngineDetailsFromSource(
        this.previewPromoEngine,
        this.previewPromoEngine?.changes,
      )
      const parsedDatiDocumenti = JSON.parse(
        getCurrentSale?.dati_documenti ?? '{}',
      )
      const newDatiDocumenti = {
        ...parsedDatiDocumenti,
        promo_engine: {
          ...parsedDatiDocumenti.promo_engine,
          promo_engine_details: updatedPromoEngineDetails,
        },
      }

      // 6) aggiorna prezzi dei capi (la preview aggiornata può cambiare prezzi finali dei capi)
      const newCapi = updateCapiBasedOnPreview(
        getCurrentSale.capi,
        this.previewPromoEngine,
      )
      const saleWithUpdatedPrices = { ...getCurrentSale, capi: newCapi }

      // 6) dati_aggiuntivi + dati_documenti: aggiorna i dati delle stringhe nella sale con i nuovi punti omaggio
      const datiAggiuntivi = JSON.stringify(this.saleDatiAggiuntivi ?? '{}')
      rootVuex.dispatch(SalesActions.SET_SALE_DATI_PROMO, saleWithUpdatedPrices)
      rootVuex.dispatch(SalesActions.UPDATE_DATI_DOCUMENTI, newDatiDocumenti)
      rootVuex.commit(SalesMutations.STORE_DATI_AGGIUNTIVI, datiAggiuntivi)

      // 7) loyalty.pointsBalance: aggiorna anche i punti dentro store loyalty
      const fidelityPointsPayload = {
        current: this.fidelityRecap.current_real_balance ?? 0,
        used: this.fidelityRecap.punti_spesi ?? 0,
        earned: this.fidelityRecap.punti_standard ?? 0,
        new: this.fidelityRecap.new_points_balance ?? 0,
        bonus: this.fidelityRecap.punti_omaggio ?? 0,
      }
      rootVuex.commit(
        LoyaltyMutations.UPDATE_CURRENT_SALE_FIDELITY_POINTS_BALANCE,
        fidelityPointsPayload,
      )

      return response
    },

    /**
     *
     * @param applyCheckoutResp
     * @returns
     */
    buildFidelityRecap(output) {
      if (!output) {
        this.fidelityRecap = null
        this.saleDatiAggiuntivi = null
        return
      }

      const items = Array.isArray(output.items) ? output.items : []
      const costs = Array.isArray(output.costs) ? output.costs : []

      const items_punti_standard = items.reduce(
        (sum, it) => sum + (Number(it.fidelity_points) || 0),
        0,
      )
      // USUALLY PURCHASED GIFT CARDS
      const costs_punti_standard = costs.reduce(
        (sum, it) => sum + (Number(it.fidelity_points) || 0),
        0,
      )
      const punti_standard = items_punti_standard + costs_punti_standard

      const punti_omaggio = items.reduce((sum, it) => {
        const pts = (it.promo_engine_points || []).reduce(
          (s, p) => s + (Number(p.value) || 0),
          0,
        )
        return sum + pts
      }, 0)

      const getCurrentSale = rootVuex.getters?.[SalesGetters.GET_CURRENT_SALE]
      const hasUsedLoyaltyPoints = getCurrentSale.pagamenti?.find(
        (p) => p.codice_movimento === 'CONTABILITA_PROMOZIONE',
      )

      let punti_spesi = hasUsedLoyaltyPoints
        ? JSON.parse(hasUsedLoyaltyPoints.dati_operazione || '{}')
            ?.last_used_value
        : 0
      if (
        punti_spesi === '' ||
        punti_spesi === undefined ||
        punti_spesi === null
      ) {
        punti_spesi = output.discount_fidelity ?? 0
      }
      punti_spesi = Number(punti_spesi) || 0

      let current_real_balance = output?.consumer?.fidelity?.card_real_balance
      if (
        current_real_balance === '' ||
        current_real_balance === undefined ||
        current_real_balance === null
      ) {
        current_real_balance = 0
      }
      current_real_balance = Number(current_real_balance) || 0

      const new_points_balance =
        current_real_balance -
        Math.abs(punti_spesi) +
        punti_standard +
        punti_omaggio

      this.fidelityRecap = {
        punti_standard,
        punti_omaggio,
        punti_spesi,
        current_real_balance,
        new_points_balance,
      }

      // console.log(items)
      const detail = items.map((it) => {
        const capoOmaggioAmount = it.promo_engine_points?.reduce(
          (acc, e) => acc + e.value,
          0,
        )
        return {
          progressivo: it.id,
          sku: it.shop_barcode,
          tipo_prezzo: it.pricelist_type,
          importo_iniziale: it.base_price,
          importo_finale: it.final_price,
          classe: it.class,
          nome: it.style_name,
          annostag: `${it.year}${it.season}`,
          modello: it.style_code,
          variante: it.style_variant,
          taglia: it.size,
          tipologia_merce: it.product_category,
          tipo: 'CAPO',
          punti: Number(it.fidelity_points) || 0,
          punti_omaggio: capoOmaggioAmount || 0,
          guadagnati: Number(it.fidelity_points) || 0,
          utilizzati: 0,
          importo_promozione: Math.abs(it.amount_correction) || 0,
        }
      })

      const cardKey =
        output?.consumer?.fidelity?.card_type || 'LOYALTY_CARD_NEW'

      this.saleDatiAggiuntivi = {
        [cardKey]: {
          last_old_value: current_real_balance,
          last_gained_value: punti_standard,
          last_promo_value: punti_omaggio,
          last_used_value: punti_spesi,
          current_value: new_points_balance,
          detail,
        },
      }
    },

    /**
     * Format payments from sale payments for promo payload and then set it in pinia store.
     * @param salePayments
     * @returns
     */
    formatPayments(salePayments) {
      const paymentsWithoutDiscounts = salePayments?.filter(
        (c: ApiV1SalesDataAttributesPagamenti) =>
          c.codice_movimento !== 'CONTABILITA_PROMOZIONE' &&
          c.codice_movimento !== 'CONTABILITA_GIFT',
      )
      const mappedPayments = paymentsWithoutDiscounts.map(
        (payment: ApiV1SalesDataAttributesPagamenti) => {
          return {
            payment_amount: payment.importo_finale,
            payment_type: payment.cod_operazione,
          }
        },
      )
      return mappedPayments
    },
  },
})

// Mapper apply Payload --> { items, costs, promo }
function mapApplyResponseToPreview(resp: any) {
  const out = resp?.output ?? {}
  const itemsOut = Array.isArray(out.items) ? out.items : []
  const costsOut = Array.isArray(out.costs) ? out.costs : []
  const changes = Array.isArray(resp?.changes) ? resp.changes : []

  const promoByCode = Object.fromEntries(changes.map((ch) => [ch.promo, ch]))

  const fidelityProgramName = out?.consumer?.fidelity?.card_type || ''

  const items = itemsOut.map((it: any) => {
    const price_before = Number(it?.original_final_price ?? 0)
    const price_after = Number(it?.final_price ?? 0)
    const discount_fidelity = Math.abs(it?.discount_fidelity ?? 0)
    const wasAnyPromoApplied = it?.promo_engine.length > 0
    return {
      sku: String(it?.shop_barcode ?? ''),
      name: String(it?.style_name ?? ''),
      list_price: Number(it?.retail_price ?? 0),
      amount_correction: Number(it?.amount_correction ?? 0),
      price_before,
      price_after,
      discount_fidelity,
      modified: wasAnyPromoApplied,
      promo_engine: Array.isArray(it?.promo_engine) ? it.promo_engine : [],
      id: it?.id ?? null,
      fidelity_points: it?.fidelity_points ?? 0,
      promo_engine_points: it?.promo_engine_points ?? [],
      promoAccordionRows: buildPromoAccordionRowsForItem(
        it,
        promoByCode,
        fidelityProgramName,
      ),
    }
  })

  const costs = costsOut.map((c: any) => {
    const price_before = Number(c?.original_final_price ?? 0)
    const price_after = Number(c?.final_price ?? 0)
    const wasAnyPromoApplied = c?.promo_engine.length > 0
    return {
      id: c?.id ?? null,
      type: String(c?.cost_type_trad ?? ''),
      list_price: price_before, // per i costi il listino coincide con original_final_price
      price_before,
      price_after,
      modified: wasAnyPromoApplied,
      sku: String(c?.sku ?? ''),
      promo_engine: Array.isArray(c?.promo_engine) ? c.promo_engine : [],
      promo_engine_points: c?.promo_engine_points ?? [],
      promoAccordionRows: buildPromoAccordionRowsForCost(
        c,
        promoByCode,
        fidelityProgramName,
      ),
    }
  })

  const promo = changes.map((ch: any) => ({
    code: ch?.promo ?? '',
    informations: ch?.informations ?? {},
    effects: Array.isArray(ch?.effects) ? ch.effects : [],
  }))

  return { items, costs, promo, changes, output: out }
}

// Mapper evaluate res per PROMO AUTO --> { input, items, costs, promo }
// + salvo input in store per checkout finale
function mapEvaluateResponseToPreview(resp: any) {
  const out = resp?.output ?? {}
  const input = resp?.input ?? {}
  const itemsOut = Array.isArray(out.items) ? out.items : []
  const costsOut = Array.isArray(out.costs) ? out.costs : []
  const changes = Array.isArray(resp?.changes) ? resp.changes : []

  // const fidelityProgramName = out?.consumer?.fidelity?.card_type || ''

  const items = itemsOut.map((it: any) => {
    const price_before = Number(it?.original_final_price ?? 0)
    const price_after = Number(it?.final_price ?? 0)
    const wasAnyPromoApplied = it?.promo_engine?.length > 0
    return {
      sku: String(it?.shop_barcode ?? ''),
      name: String(it?.style_name ?? ''),
      list_price: Number(it?.retail_price ?? 0),
      prev_price: price_before,
      final_price: price_after,
      net_final_price: price_after,
      discount_on_item: Math.abs(it?.amount_correction ?? 0),
      discount_final_price: Number(it?.retail_price ?? 0),
      modified: wasAnyPromoApplied,
      promo_engine: Array.isArray(it?.promo_engine) ? it.promo_engine : [],
      id: it?.id ?? null,
      fidelity_points: it?.fidelity_points ?? 0,
      promo_engine_points: it?.promo_engine_points ?? [],
    }
  })

  const costs = costsOut.map((cost: any) => {
    const price_before = Number(cost?.original_final_price ?? 0)
    const price_after = Number(cost?.final_price ?? cost?.cost_amount ?? 0)
    const wasAnyPromoApplied = cost?.promo_engine?.length > 0
    return {
      id: cost?.id ?? null,
      type: String(cost?.cost_type_trad ?? ''),
      list_price: price_before, // per i costi il listino coincide con original_final_price
      prev_price: price_before,
      final_price: price_after,
      net_final_price: price_after,
      discount_on_item: Math.abs(cost?.amount_correction ?? 0),
      discount_final_price: Number(cost?.retail_price ?? 0),
      modified: wasAnyPromoApplied,
      sku: String(cost?.sku ?? ''),
      promo_engine: Array.isArray(cost?.promo_engine) ? cost.promo_engine : [],
    }
  })

  const mapInnerItems = (effect) => (item: any) => ({
    id: item.id,
    amount: item?.amount_correction?.diff || item?.amount_correction?.end || 0,
    value_type: effect?.value_type,
    value:
      item?.discount_percentage?.diff || item?.discount_percentage?.end || 0,
  })

  const promo = changes.reduce((result, item) => {
    result[item.promo] = {
      id_promo: item.promo,
      description: item.informations.description,
      title: item.informations.title,
      costs: [],
      items: item.effects.flatMap((effect: any) =>
        effect.items.map(mapInnerItems(effect)),
      ),
    }
    return result
  }, {})

  return { input, items, costs, promo, changes }
}

/**
 * Costruisce le righe per il dettaglio accordion di un item
 */
export function buildPromoAccordionRowsForItem(
  item: any,
  promoByCode: Record<string, any>,
  fidelityProgramName = '',
) {
  if (!Array.isArray(item?.promo_engine)) {
    return []
  }

  return item.promo_engine
    .map((code: string) => {
      const promo = promoByCode[code]
      if (!promo) {
        return null
      }

      const info = promo.informations ?? {}
      const effects = Array.isArray(promo.effects) ? promo.effects : []
      const counter = promo.promotion_counter ?? {}
      const firstEffect = effects[0] ?? {}
      const firstItem =
        Array.isArray(firstEffect.items) && firstEffect.items.length
          ? firstEffect.items[item.id - 1]
          : null

      // campi principali
      const promoId = code.replace(/^promo_/, '') // es. "promo_761" → "761"
      const descrizione = info.title || info.description || `Promo ${promoId}`

      const utilizzo =
        counter && counter.max_counter
          ? `${counter.current_counter}/${counter.max_counter}`
          : '∞'

      // date
      const puntiDa = firstEffect.start_date ? firstEffect.start_date : '-'
      const puntiA = firstEffect.end_date ? firstEffect.end_date : '-'

      // omaggio
      let omaggio = '-'
      if (firstEffect.value_type?.includes('POINTS')) {
        omaggio = firstItem?.value ? `+${firstItem.value}` : '-'
      }
      // TODO: accertarsi con BE che i dati siano solo quelli dei punti e non di altro tipo... es percentuali, esistono punti in percentuale?

      // programma
      // TODO: chiedere al BE il dato corretto "Nome programma"? // ?.replace(/_/g, ' ')
      const programma = fidelityProgramName || '-'

      return {
        promo: promoId,
        descrizione,
        utilizzo,
        puntiDa,
        puntiA,
        omaggio,
        programma,
      }
    })
    .filter(Boolean)
}

/**
 * Costruisce le righe per il dettaglio accordion di un cost.
 * Uguale alla funzione sopra ma prende i cost (gift card, ecc) di riferimento
 */
export function buildPromoAccordionRowsForCost(
  cost: any,
  promoByCode: Record<string, any>,
  fidelityProgramName = '',
) {
  if (!Array.isArray(cost?.promo_engine)) {
    return []
  }

  return cost.promo_engine
    .map((code: string) => {
      const promo = promoByCode[code]
      if (!promo) {
        return null
      }

      const info = promo.informations ?? {}
      const effects = Array.isArray(promo.effects) ? promo.effects : []
      const counter = promo.promotion_counter ?? {}
      const firstEffect = effects[0] ?? {}
      const firstItem =
        Array.isArray(firstEffect.costs) && firstEffect.costs.length
          ? firstEffect.costs[cost.id - 1]
          : null

      // campi principali
      const promoId = code.replace(/^promo_/, '') // es. "promo_761" → "761"
      const descrizione = info.title || info.description || `Promo ${promoId}`

      const utilizzo =
        counter && counter.max_counter
          ? `${counter.current_counter}/${counter.max_counter}`
          : '∞'

      // date
      const puntiDa = firstEffect.start_date ? firstEffect.start_date : '-'
      const puntiA = firstEffect.end_date ? firstEffect.end_date : '-'

      // omaggio
      let omaggio = '-'
      if (firstEffect.value_type?.includes('POINTS')) {
        omaggio = firstItem?.value ? `+${firstItem.value}` : '-'
      }
      // TODO: accertarsi con BE che i dati siano solo quelli dei punti e non di altro tipo... es percentuali, esistono punti in percentuale?

      // programma
      // TODO: chiedere al BE il dato corretto "Nome programma"? // ?.replace(/_/g, ' ')
      const programma = fidelityProgramName || '-'

      return {
        promo: promoId,
        descrizione,
        utilizzo,
        puntiDa,
        puntiA,
        omaggio,
        programma,
      }
    })
    .filter(Boolean)
}

export type PromoEngineStore = ReturnType<typeof usePromoEngineStore>
